import {
  assignmentQuestionnaireName,
  looksLikeInternalTemplateId,
  type QuestionnaireTemplateName,
} from '@/lib/questionnaireDisplayName';

export type QuestionnaireSendPhase = 'before' | 'after';

export type QuestionnaireHistoryAppointment = {
  id: string;
  starts_at: string | null;
  service_name?: string | null;
  services?: { name?: string | null } | null;
};

export type QuestionnaireHistoryAssignment = {
  id: string;
  forms_assignment_id: string;
  forms_template_id: string;
  appointment_id?: string | null;
  template_summary?: { name?: string | null; locale?: string | null } | null;
  metadata?: Record<string, unknown> | null;
  status: string;
  sent_at: string | null;
  due_at: string | null;
  appointments?: QuestionnaireHistoryAppointment | QuestionnaireHistoryAppointment[] | null;
};

export type QuestionnaireHistoryRow = {
  assignmentId: string;
  formsAssignmentId: string;
  formsTemplateId: string;
  questionnaireName: string;
  nameResolved: boolean;
  serviceName: string | null;
  appointmentStartsAt: string | null;
  hasAppointment: boolean;
  phase: QuestionnaireSendPhase | null;
  sentAt: string | null;
  dueAt: string | null;
  status: string;
};

export function resolveQuestionnairePhase(
  metadata: Record<string, unknown> | null | undefined,
): QuestionnaireSendPhase | null {
  const sentVia = metadata?.sent_via;
  if (sentVia === 'pre_visit') return 'before';
  if (sentVia === 'auto') return 'after';
  return null;
}

export function embeddedAppointment(
  assignment: QuestionnaireHistoryAssignment,
): QuestionnaireHistoryAppointment | null {
  const raw = assignment.appointments;
  if (!raw) return null;
  return Array.isArray(raw) ? (raw[0] ?? null) : raw;
}

export function resolveServiceName(
  appointment: QuestionnaireHistoryAppointment | null,
): string | null {
  if (!appointment) return null;
  const fromService = appointment.services?.name?.trim();
  if (fromService) return fromService;
  const fromDenorm = appointment.service_name?.trim();
  return fromDenorm || null;
}

export function buildQuestionnaireHistoryRow(
  assignment: QuestionnaireHistoryAssignment,
  templatesById: Map<string, QuestionnaireTemplateName>,
  locale: string,
  fallbackName: string,
): QuestionnaireHistoryRow {
  const appointment = embeddedAppointment(assignment);
  const questionnaireName = assignmentQuestionnaireName(
    assignment,
    templatesById,
    locale,
    fallbackName,
  );
  const nameResolved =
    Boolean(questionnaireName.trim()) &&
    questionnaireName !== fallbackName &&
    !looksLikeInternalTemplateId(questionnaireName);

  return {
    assignmentId: assignment.id,
    formsAssignmentId: assignment.forms_assignment_id,
    formsTemplateId: assignment.forms_template_id,
    questionnaireName,
    nameResolved,
    serviceName: resolveServiceName(appointment),
    appointmentStartsAt: appointment?.starts_at ?? null,
    hasAppointment: Boolean(assignment.appointment_id || appointment),
    phase: resolveQuestionnairePhase(assignment.metadata),
    sentAt: assignment.sent_at,
    dueAt: assignment.due_at,
    status: assignment.status,
  };
}

export function buildQuestionnaireHistoryRows(
  assignments: QuestionnaireHistoryAssignment[],
  templatesById: Map<string, QuestionnaireTemplateName>,
  locale: string,
  fallbackName: string,
): QuestionnaireHistoryRow[] {
  return assignments.map((assignment) =>
    buildQuestionnaireHistoryRow(assignment, templatesById, locale, fallbackName),
  );
}

export function historySecondaryLine(
  row: QuestionnaireHistoryRow,
  labels: {
    before: string;
    after: string;
    unlinked: string;
  },
): string | null {
  const phaseLabel =
    row.phase === 'before' ? labels.before : row.phase === 'after' ? labels.after : null;
  if (row.serviceName && phaseLabel) return `${row.serviceName} · ${phaseLabel}`;
  if (row.serviceName) return row.serviceName;
  if (phaseLabel) return phaseLabel;
  if (!row.hasAppointment) return labels.unlinked;
  return null;
}
